class DropdownMenu extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      hover: 'is-hover',
      active: 'is-active',
      animate: 'slide-from-top',
    };
    this.selectors = {
      header: '.shopify-section-header',
      background: '.header-background',
      topMenuItem: '.header__menu-item--top',
      menus: 'dropdown-menu, mega-menu',
      wrapper: '.list-menu',
      childAnimate: '.list-menu-dropdown > .list-menu'
    };
    this.timeout;
    this.closeTimeout = null;
    this.openTimeout = null;

    // Click to open/close dropdown
    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('keydown', this.handleKeydown.bind(this));
    // Hover on span.label to open dropdown
    const topLink = this.querySelector(this.selectors.topMenuItem);
    if (topLink) {
      const labelSpan = topLink.querySelector('span.label');
      if (labelSpan) {
        labelSpan.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
      }
    }
    // Keep menu open when hovering dropdown content
    const dropdownContent = this.querySelector('.list-menu-dropdown');
    if (dropdownContent) {
      dropdownContent.addEventListener('mouseenter', () => this.cancelAllCloseTimeouts());
    }
    this.addEventListener('mouseleave', this.handleMouseLeave.bind(this));
    // Close when clicking outside
    this.onDocumentClick = this.handleDocumentClick.bind(this);
  }

  handleMouseEnter() {
    // If no dropdown exists, close any open menus and do nothing else
    if (!this.querySelector('.list-menu-dropdown')) {
      this.bindEvent();
      if (this.header) {
        const menus = this.header.querySelectorAll(this.selectors.menus);
        menus.forEach((menu) => {
          if (menu.closeTimeout) {
            clearTimeout(menu.closeTimeout);
            menu.closeTimeout = null;
          }
          if (menu.classList.contains(this.classes.active)) {
            menu.reset();
            menu.resetBackground();
            this.header.classList.remove(this.classes.hover);
          }
        });
      }
      return;
    }

    // Cancel any pending close on any menu
    this.cancelAllCloseTimeouts();

    if (this.classList.contains(this.classes.active)) return;

    // Clear any previous open timer
    if (this.openTimeout) {
      clearTimeout(this.openTimeout);
      this.openTimeout = null;
    }

    // Small delay to prevent rapid / accidental triggering
    this.openTimeout = setTimeout(() => {
      this.openTimeout = null;
      if (!this.classList.contains(this.classes.active)) {
        this.bindEvent();
        this.removeClasses();
        this.addClasses();
        this.resetBackground();
        this.animateMenu();
      }
    }, 350);
  }

  handleMouseLeave(event) {
    // Cancel pending open if the cursor leaves before the delay fires
    if (this.openTimeout) {
      clearTimeout(this.openTimeout);
      this.openTimeout = null;
    }

    if (!this.header) return;
    
    // Check if we're moving to the dropdown content
    const dropdown = this.querySelector('.list-menu-dropdown');
    const relatedTarget = event.relatedTarget;
    if (dropdown && dropdown.contains(relatedTarget)) return;
    
    // Add delay before closing to allow cursor to reach dropdown
    this.closeTimeout = setTimeout(() => {
      this.reset();
      this.resetBackground();
      this.header.classList.remove(this.classes.hover);
    }, 300);
  }

  cancelAllCloseTimeouts() {
    // Cancel close timeouts on all menus
    const header = document.querySelector(this.selectors.header);
    if (header) {
      const menus = header.querySelectorAll(this.selectors.menus);
      menus.forEach((menu) => {
        if (menu.closeTimeout) {
          clearTimeout(menu.closeTimeout);
          menu.closeTimeout = null;
        }
      });
    }
  }

  handleClick(event) {
    const topMenuItem = this.querySelector(this.selectors.topMenuItem);
    if (!topMenuItem) return;
    
    // Only handle clicks on the top menu item link
    const clickedLink = event.target.closest('.header__menu-item--top');
    if (!clickedLink) return;

    // If no dropdown exists, let the link navigate normally
    const hasDropdown = !!this.querySelector('.list-menu-dropdown');
    if (!hasDropdown) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    const isActive = this.classList.contains(this.classes.active);
    
    this.bindEvent();
    this.removeClasses();
    
    if (!isActive) {
      this.addClasses();
      this.resetBackground();
      this.animateMenu();
      document.addEventListener('click', this.onDocumentClick);
    } else {
      this.reset();
      this.resetBackground();
      this.header.classList.remove(this.classes.hover);
      document.removeEventListener('click', this.onDocumentClick);
    }
  }

  handleDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.reset();
      this.resetBackground();
      if (this.header) {
        this.header.classList.remove(this.classes.hover);
      }
      document.removeEventListener('click', this.onDocumentClick);
    }
  }

  bindEvent() {
    this.header = this.header || document.querySelector(this.selectors.header);
    this.wrapper = this.closest(this.selectors.wrapper);
  }

  addClasses() {
    this.updateHeaderHeight();
    this.classList.add(this.classes.active);
    this.header.classList.add(this.classes.hover);
    this.showOverlay();
  }

  showOverlay() {
    let overlay = document.querySelector('.dropdown-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'dropdown-overlay';
      document.body.appendChild(overlay);
      overlay.addEventListener('click', () => this.close());
    }
    // Force reflow before adding class
    overlay.offsetHeight;
    overlay.classList.add('is-visible');
  }

  hideOverlay() {
    // Only hide overlay if no other menu is active
    const header = document.querySelector(this.selectors.header);
    if (header) {
      const activeMenus = header.querySelectorAll('dropdown-menu.is-active, mega-menu.is-active');
      if (activeMenus.length > 0) return;
    }
    
    const overlay = document.querySelector('.dropdown-overlay');
    if (overlay) {
      overlay.classList.remove('is-visible');
    }
  }

  updateHeaderHeight() {
    // Get the actual header wrapper element's height
    const headerWrapper = document.querySelector('.header-wrapper');
    const headerElement = document.querySelector('.shopify-section-header .header');
    let totalHeaderHeight = 0;
    
    if (headerWrapper) {
      const rect = headerWrapper.getBoundingClientRect();
      totalHeaderHeight = rect.bottom;
    } else if (headerElement) {
      const rect = headerElement.getBoundingClientRect();
      totalHeaderHeight = rect.bottom;
    } else {
      // Fallback: Use full header group height
      const headerGroups = document.querySelectorAll('.shopify-section-group-header-group');
      headerGroups.forEach(headerGroup => {
        totalHeaderHeight += headerGroup.offsetHeight;
      });
    }
    
    if (totalHeaderHeight > 0) {
      document.documentElement.style.setProperty('--header-height', `${totalHeaderHeight}px`);
    }
  }

  removeClasses() {
    const menus = this.header.querySelectorAll(this.selectors.menus);

    menus.forEach((menu) => {
      let found = false;
      const childMenus = menu.querySelectorAll('dropdown-menu');
      childMenus.forEach((element) => {
        if (element === this) {
          found = true;
          return;
        }
      });

      if (!found) {
        // Clear the menu's close timeout before resetting
        if (menu.closeTimeout) {
          clearTimeout(menu.closeTimeout);
          menu.closeTimeout = null;
        }
        menu.reset();
      }
    });
  }

  isHover() {
    return this.header.classList.contains(this.classes.hover);
  }

  reset() {
    // Clear any pending close timeout
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
    
    this.classList.remove(this.classes.active);

    const childAnimate = this.querySelector(this.selectors.childAnimate);
    if (childAnimate) childAnimate.classList.remove(this.classes.animate);

    clearTimeout(this.timeout);
    
    // Delay hiding overlay to allow other menu to take over
    setTimeout(() => this.hideOverlay(), 10);
  }

  resetBackground() {
    const background = this.header.querySelector(this.selectors.background);
    background.classList.remove(this.classes.active);
  }

  animateMenu() {
    const childAnimate =  this.querySelector(this.selectors.childAnimate);
    if (childAnimate) {
      setTimeout(() => {
        childAnimate.classList.add(this.classes.animate);
      }, 150);
    }
  }

  close() {
    this.reset();
    this.resetBackground();
    if (this.header) {
      this.header.classList.remove(this.classes.hover);
    }
    document.removeEventListener('click', this.onDocumentClick);
  }

  handleKeydown(event) {
    const ARROW_UP = ['ArrowUp', 'Up'];
    const ARROW_DOWN = ['ArrowDown', 'Down'];
    const ARROW_LEFT = ['ArrowLeft', 'Left'];
    const ARROW_RIGHT = ['ArrowRight', 'Right'];
    const ESCAPE = ['Escape', 'Esc'];
    
    if (ESCAPE.includes(event.key)) {
      this.close();
      return;
    }
    
    const items = Array.from(this.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])'));
    const activeElement = document.activeElement;
    const currentIndex = items.indexOf(activeElement);
    if (currentIndex === -1) return;

    if (ARROW_DOWN.includes(event.key)) {
      event.preventDefault();
      const nextIndex = (currentIndex + 1) % items.length;
      items[nextIndex].focus();
    } else if (ARROW_UP.includes(event.key)) {
      event.preventDefault();
      const prevIndex = (currentIndex - 1 + items.length) % items.length;
      items[prevIndex].focus();
    } else if (ARROW_RIGHT.includes(event.key)) {
      // Try to move right to the next column if possible
      event.preventDefault();
      const nextColumn = activeElement.closest('.mega-menu__item, .list-menu-dropdown')?.nextElementSibling;
      if (nextColumn) {
        const nextFocusable = nextColumn.querySelector('a, button, [tabindex]:not([tabindex="-1"])');
        if (nextFocusable) nextFocusable.focus();
      }
    } else if (ARROW_LEFT.includes(event.key)) {
      // Try to move left to the previous column if possible
      event.preventDefault();
      const prevColumn = activeElement.closest('.mega-menu__item, .list-menu-dropdown')?.previousElementSibling;
      if (prevColumn) {
        const prevFocusable = prevColumn.querySelector('a, button, [tabindex]:not([tabindex="-1"])');
        if (prevFocusable) prevFocusable.focus();
      }
    }
  }

}
customElements.define('dropdown-menu', DropdownMenu);

class MegaMenu extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      hover: 'is-hover',
      active: 'is-active',
      animate: 'slide-from-top',
    };
    this.selectors = {
      header: '.shopify-section-header',
      background: '.header-background',
      dropdown: '.list-menu-dropdown',
      menus: 'dropdown-menu, mega-menu',
      wrapper: '.list-menu',
      childAnimate: '.mega-menu__item, .mega-menu__promo'
    };
    this.timeout = [];
    this.closeTimeout = null;
    this.openTimeout = null;
    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('keydown', this.handleKeydown.bind(this));
    // Hover on span.label to open mega menu
    const topLink = this.querySelector('.header__menu-item--top');
    if (topLink) {
      const labelSpan = topLink.querySelector('span.label');
      if (labelSpan) {
        labelSpan.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
      }
    }
    // Keep menu open when hovering dropdown content
    const dropdownContent = this.querySelector('.list-menu-dropdown');
    if (dropdownContent) {
      dropdownContent.addEventListener('mouseenter', () => this.cancelAllCloseTimeouts());
    }
    this.addEventListener('mouseleave', this.handleMouseLeave.bind(this));
    // Close when clicking outside
    this.onDocumentClick = this.handleDocumentClick.bind(this);
  }

  handleMouseEnter() {
    // Cancel any pending close on any menu
    this.cancelAllCloseTimeouts();
    
    if (this.classList.contains(this.classes.active)) return;

    // Clear any previous open timer
    if (this.openTimeout) {
      clearTimeout(this.openTimeout);
      this.openTimeout = null;
    }

    // Small delay to prevent rapid / accidental triggering
    this.openTimeout = setTimeout(() => {
      this.openTimeout = null;
      if (!this.classList.contains(this.classes.active)) {
        this.bindEvent();
        this.removeClasses();
        this.addClasses();
        this.updateHeaderHeight();
        this.showSublist();
      }
    }, 350);
  }

  handleMouseLeave(event) {
    // Cancel pending open if the cursor leaves before the delay fires
    if (this.openTimeout) {
      clearTimeout(this.openTimeout);
      this.openTimeout = null;
    }

    if (!this.header) return;
    
    // Check if we're moving to the dropdown content
    const dropdown = this.querySelector('.list-menu-dropdown');
    const relatedTarget = event.relatedTarget;
    if (dropdown && dropdown.contains(relatedTarget)) return;
    
    // Add delay before closing to allow cursor to reach dropdown
    this.closeTimeout = setTimeout(() => {
      this.reset();
      this.resetBackground();
      this.header.classList.remove(this.classes.hover);
    }, 300);
  }

  cancelAllCloseTimeouts() {
    // Cancel close timeouts on all menus
    const header = document.querySelector(this.selectors.header);
    if (header) {
      const menus = header.querySelectorAll(this.selectors.menus);
      menus.forEach((menu) => {
        if (menu.closeTimeout) {
          clearTimeout(menu.closeTimeout);
          menu.closeTimeout = null;
        }
      });
    }
  }

  handleClick(event) {
    const topMenuItem = this.querySelector('.header__menu-item--top');
    if (!topMenuItem) return;
    
    // If clicking the top menu item link, allow normal navigation to its URL
    const clickedLink = event.target.closest('.header__menu-item--top');
    if (clickedLink) return;
  }

  handleDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.reset();
      this.resetBackground();
      if (this.header) {
        this.header.classList.remove(this.classes.hover);
      }
      document.removeEventListener('click', this.onDocumentClick);
    }
  }

  bindEvent() {
    this.header = this.header || document.querySelector(this.selectors.header);
    this.wrapper = this.closest(this.selectors.wrapper);
  }

  showSublist() {
    const dropdown = this.querySelector(this.selectors.dropdown);
    const background = this.header.querySelector(this.selectors.background);
    background.style.setProperty('--height', this.header.clientHeight + dropdown.clientHeight - 16 + 'px');
    background.classList.add(this.classes.active);
  }

  addClasses() {
    this.classList.add(this.classes.active);

    const childAnimate = this.querySelectorAll(this.selectors.childAnimate);
    const delay = childAnimate.length > 5 ? 75 : 250;
    childAnimate.forEach((element, index) => {
      const timeout = setTimeout(() => {
        element.classList.add(this.classes.animate);
      }, (delay * index) + 100);
      
      this.timeout.push(timeout);
    });
    
    this.header.classList.add(this.classes.hover);
    this.showOverlay();
  }

  showOverlay() {
    let overlay = document.querySelector('.dropdown-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'dropdown-overlay';
      document.body.appendChild(overlay);
      overlay.addEventListener('click', () => this.close());
    }
    // Force reflow before adding class
    overlay.offsetHeight;
    overlay.classList.add('is-visible');
  }

  hideOverlay() {
    // Only hide overlay if no other menu is active
    const header = document.querySelector(this.selectors.header);
    if (header) {
      const activeMenus = header.querySelectorAll('dropdown-menu.is-active, mega-menu.is-active');
      if (activeMenus.length > 0) return;
    }
    
    const overlay = document.querySelector('.dropdown-overlay');
    if (overlay) {
      overlay.classList.remove('is-visible');
    }
  }

  removeClasses() {
    const menus = this.header.querySelectorAll(this.selectors.menus);
    menus.forEach((menu) => {
      // Clear the menu's close timeout before resetting
      if (menu.closeTimeout) {
        clearTimeout(menu.closeTimeout);
        menu.closeTimeout = null;
      }
      menu.reset();
    });
  }

  reset() {
    // Clear any pending close timeout
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
    
    this.classList.remove(this.classes.active);

    const childAnimate = this.querySelectorAll(this.selectors.childAnimate);
    childAnimate.forEach((element) => {
      element.classList.remove(this.classes.animate);
    });
    this.timeout.forEach((timeout) => {
      clearTimeout(timeout);
    });
    
    // Delay hiding overlay to allow other menu to take over
    setTimeout(() => this.hideOverlay(), 10);
  }

  resetBackground() {
    const background = this.header.querySelector(this.selectors.background);
    background.classList.remove(this.classes.active);
  }

  close() {
    this.reset();
    this.resetBackground();
    if (this.header) {
      this.header.classList.remove(this.classes.hover);
    }
    document.removeEventListener('click', this.onDocumentClick);
  }

  handleKeydown(event) {
    const ARROW_UP = ['ArrowUp', 'Up'];
    const ARROW_DOWN = ['ArrowDown', 'Down'];
    const ARROW_LEFT = ['ArrowLeft', 'Left'];
    const ARROW_RIGHT = ['ArrowRight', 'Right'];
    const ESCAPE = ['Escape', 'Esc'];
    
    if (ESCAPE.includes(event.key)) {
      this.close();
      return;
    }
    
    const items = Array.from(this.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])'));
    const activeElement = document.activeElement;
    const currentIndex = items.indexOf(activeElement);
    if (currentIndex === -1) return;

    if (ARROW_DOWN.includes(event.key)) {
      event.preventDefault();
      const nextIndex = (currentIndex + 1) % items.length;
      items[nextIndex].focus();
    } else if (ARROW_UP.includes(event.key)) {
      event.preventDefault();
      const prevIndex = (currentIndex - 1 + items.length) % items.length;
      items[prevIndex].focus();
    } else if (ARROW_RIGHT.includes(event.key)) {
      // Try to move right to the next column if possible
      event.preventDefault();
      const nextColumn = activeElement.closest('.mega-menu__item, .list-menu-dropdown')?.nextElementSibling;
      if (nextColumn) {
        const nextFocusable = nextColumn.querySelector('a, button, [tabindex]:not([tabindex="-1"])');
        if (nextFocusable) nextFocusable.focus();
      }
    } else if (ARROW_LEFT.includes(event.key)) {
      // Try to move left to the previous column if possible
      event.preventDefault();
      const prevColumn = activeElement.closest('.mega-menu__item, .list-menu-dropdown')?.previousElementSibling;
      if (prevColumn) {
        const prevFocusable = prevColumn.querySelector('a, button, [tabindex]:not([tabindex="-1"])');
        if (prevFocusable) prevFocusable.focus();
      }
    }
  }

  updateHeaderHeight() {
    // Get the actual header wrapper element's height
    const headerWrapper = document.querySelector('.header-wrapper');
    const headerElement = document.querySelector('.shopify-section-header .header');
    let totalHeaderHeight = 0;
    
    if (headerWrapper) {
      const rect = headerWrapper.getBoundingClientRect();
      totalHeaderHeight = rect.bottom;
    } else if (headerElement) {
      const rect = headerElement.getBoundingClientRect();
      totalHeaderHeight = rect.bottom;
    } else {
      // Fallback: Use full header group height
      const headerGroups = document.querySelectorAll('.shopify-section-group-header-group');
      headerGroups.forEach(headerGroup => {
        totalHeaderHeight += headerGroup.offsetHeight;
      });
    }
    
    if (totalHeaderHeight > 0) {
      document.documentElement.style.setProperty('--header-height', `${totalHeaderHeight}px`);
    }
  }
}
customElements.define('mega-menu', MegaMenu);
